import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "@phosphor-icons/react";
import PageHero from "@/components/PageHero";
import { FadeUp, ImageReveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import usePageMeta from "@/lib/usePageMeta";

const POINTS = [
  "Hoveddøre, terrassedøre og større dørpartier",
  "Materialer, farver, greb og glaspartier",
  "Lysindfald, adgangsforhold, sikkerhed og tæthed",
  "Tilpasning, montering og finish omkring døren",
];

export default function Doors() {
  usePageMeta({
    title: "Døre",
    description:
      "Døre, der passer naturligt til dit hus. Vi rådgiver om materialer, proportioner og glaspartier — hoveddøre, terrassedøre og dørpartier fra Hagelund.",
    path: "/doere",
    image: "/images/skovvej-15.webp",
  });

  return (
    <div data-testid="doors-page">
      <PageHero
        eyebrow="Specialister i døre"
        titleLines={["Døre, der passer", "naturligt til dit hus"]}
        image="/images/skovvej-15.webp"
        kicker="En ny dør skal både byde velkommen, fungere i hverdagen og spille sammen med husets arkitektur. Vi rådgiver om materialer, proportioner, farver, glaspartier og funktion, så du får en gennemtænkt løsning fra første indtryk til daglig brug."
      />

      <section className="mx-auto max-w-[1600px] px-6 md:px-12 py-24 md:py-32">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
          <div className="md:col-span-5">
            <FadeUp>
              <p className="text-xs uppercase tracking-label text-accent mb-6">Vores tilgang</p>
              <h2 className="font-display text-4xl md:text-5xl font-light leading-tight text-balance">
                Vi finder døren, der passer til både huset og hverdagen
              </h2>
              <p className="mt-6 text-muted-foreground font-light text-lg">
                En ny dør ændrer mere end selve indgangen. Den har betydning for facadens samlede udtryk, lysindfaldet, komforten og den måde, du bruger dit hjem på hver dag. Derfor starter vi ikke med et produktkatalog, men med at forstå huset og dine ønsker. Vi gennemgår de eksisterende forhold og rådgiver om de valg, der har betydning for det færdige resultat. På den baggrund anbefaler vi en løsning, hvor dørtype, materialer, farver og detaljer spiller sammen med boligens arkitektur — og fungerer i praksis mange år frem.
              </p>
              <ul className="mt-10 space-y-4">
                {POINTS.map((p) => (
                  <li key={p} className="flex items-start gap-3 font-light">
                    <Check size={20} weight="light" className="text-accent mt-1 shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
              <Link to="/kontakt" data-testid="doors-cta" className="group inline-flex items-center gap-3 mt-10 bg-foreground text-background px-8 py-4 text-xs uppercase tracking-label font-semibold hover:bg-accent transition-colors">
                Book rådgivning <ArrowRight size={16} weight="bold" className="transition-transform group-hover:translate-x-1" />
              </Link>
            </FadeUp>
          </div>
          <div className="md:col-span-7 grid grid-cols-2 gap-4 md:gap-6">
            <ImageReveal src="/images/christiansminde-16.webp" alt="Dør detalje" className="aspect-[3/4] row-span-2" />
            <ImageReveal src="/images/skovvej-17.webp" alt="Indendørs entré med egetræsdør" className="aspect-square" delay={0.1} />
            <ImageReveal src="/images/skovvej-16.webp" alt="Facadedør og glasparti" className="aspect-square" delay={0.2} />
          </div>
        </div>
      </section>

      <Marquee />

      <section className="mx-auto max-w-[1600px] px-6 md:px-12 py-24 md:py-32">
        <FadeUp>
          <blockquote className="font-display text-3xl md:text-5xl font-light leading-[1.15] max-w-4xl text-balance">
            “Vi starter ikke med et produkt. Vi starter med dit hus.”
          </blockquote>
        </FadeUp>
      </section>
    </div>
  );
}
